import { Component, NgZone, Optional } from '@angular/core';
import { IonApp, IonRouterOutlet, Platform, ToastController } from '@ionic/angular/standalone';
import { App } from '@capacitor/app';
import { Toast } from '@capacitor/toast';
import { ActionPerformed, PushNotificationSchema, PushNotifications, Token } from '@capacitor/push-notifications';
import { DataService } from './services/data';
import { notificationsOutline } from 'ionicons/icons';
import { addIcons } from 'ionicons';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { register } from 'swiper/element/bundle';
import { LanguageService } from "./services/Language.Service";
import { TranslateModule } from "@ngx-translate/core";

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet, TranslateModule],
})
export class AppComponent {

  isProduction = environment.production;
  lastBackPress = 0;
  timePeriodToExit = 2000; // 2 seconds


  constructor(
    private dataService: DataService,
    private platform: Platform,
    private toastController: ToastController,
    private router: Router,
    private languageService: LanguageService,
    private zone: NgZone,
    @Optional() private routerOutlet?: IonRouterOutlet
  ) {
    addIcons({ notificationsOutline });

    this.platform.backButton.subscribeWithPriority(-1, async () => {
      if (!this.routerOutlet?.canGoBack()) {
        const now = new Date().getTime();

        if (now - this.lastBackPress < this.timePeriodToExit) {
          // Exit app on second tap within 2s
          App.exitApp();
        } else {
          this.lastBackPress = now;
          await Toast.show({
            text: 'Tap back again to exit',
            duration: 'short'
          });
        }
      }
    });

    this.languageService.init();
  }




  ngOnInit() {
    if (Capacitor.getPlatform() !== 'web') {
      this.initPushNotifications();
    }
    else {
      this.initPwaPushNotifications();
    }
  }

  // PWA PUSH NOTIFICATIONS
  async initPwaPushNotifications() {

    // Dynamic import for firebase package for pwa. this will not use in native apps.
    const { initializeApp } = await import('firebase/app');
    const { getMessaging, getToken, onMessage, isSupported } = await import('firebase/messaging');

    // Bail early on browsers/contexts without web push (iOS Safari tab, Firefox private, etc.)
    if (!(await isSupported())) {
      console.warn('Web push not supported here');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      // same "show some error" slot as your native path
      return;
    }

    try {
      const app = initializeApp(environment.firebase);
      const messaging = getMessaging(app);

      // Register the FCM worker under its own scope so it can't fight with ngsw-worker.js
      const cfg = environment.firebase;
      const params = new URLSearchParams({
        apiKey: cfg.apiKey,
        projectId: cfg.projectId,
        messagingSenderId: cfg.messagingSenderId,
        appId: cfg.appId,
      });

      const swReg = await navigator.serviceWorker.register(
        `/pwa-fcm-sw.js?${params.toString()}`,
        { scope: '/firebase-cloud-messaging-push-scope' }
      );

      const token = await getToken(messaging, {
        vapidKey: environment.firebaseWebPushCertificate,
        serviceWorkerRegistration: swReg,
      });

      if (token) {
        localStorage.setItem('fcmToken', token);   // mirrors your native 'registration' handler
      }

      // Foreground — equivalent of 'pushNotificationReceived'
      onMessage(messaging, (payload) => {
        const title = payload.notification?.title ?? payload.data?.['title'];
        const body = payload.notification?.body ?? payload.data?.['body'];
        this.showInAppNotification(title, body);
      });

      // Click — equivalent of 'pushNotificationActionPerformed'
      navigator.serviceWorker.addEventListener('message', (event: any) => {
        if (event.data?.type === 'PUSH_CLICK') {
          this.performePushActoin(event.data.payload);
        }
      });

    } catch (err) {
      this.dataService.showToast('Failed to register push notifications');
    }
  }
  // END PWA PUSH NOTIFICATIONS

  initPushNotifications() {
    // Request permission to use push notifications
    // iOS will prompt user and return if they granted permission or not
    // Android will just grant without prompting
    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        PushNotifications.register();
      } else {
        // Show some error
      }
    });

    PushNotifications.addListener('registration', (token: Token) => {
      localStorage.setItem('fcmToken', token.value);
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      this.dataService.showToast('Failed to register push notifications');
    });

    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      this.showInAppNotification(notification.title, notification.body)
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      this.performePushActoin(notification.notification)
    });
  }

  performePushActoin(notification: any) {
    if (notification && notification.data) {
      setTimeout(() => {
        this.zone.run(() => {
          this.handleNotificationAction(notification.data);
        });
      }, 300);
    } else {
      this.zone.run(() => {
        this.router.navigate(['/notifications']);
      });
    }
  }

  private handleNotificationAction(data: any) {
    const eventType = data.eventType;
    const searchId = data.searchId;

    if (!eventType) {
      this.router.navigate(['/notifications']);
      return;
    }

    const isEventRelated = [
      'NEW_EVENT_ADD',
      'EVENT_UPDATE',
      'EVENT_DELETE',
      'EVENT_FIRST_REMINDER',
      'EVENT_SECOND_REMINDER'
    ].includes(eventType);

    switch (eventType) {
      case 'CUSTOM_PUSH_NOTIFICATION':
      case 'OPENING_HOURS_UPDATE':
        this.router.navigate(['/tabs/home']);
        break;

      case 'APPROVE_GROUP':
      case 'REJECT_GROUP':
      case 'REQUEST_GROUP':
      case 'LEAVE_GROUP':
      case 'ADDED_AS_GROUP_ADMIN':
      case 'REMOVED_AS_GROUP_ADMIN':
      case 'WITHDREW_GROUP_REQUEST':
      case 'ADDED_TO_GROUP':
      case 'JOIN_GROUP_ACCEPTED':
        this.router.navigate(['/groups']);
        break;

      case 'PARTNER_REQUEST_RECEIVED':
      case 'PARTNER_REQUEST_WITHDRAWN':
      case 'PARTNER_REQUEST_ACCEPTED':
      case 'PARTNER_REQUEST_REJECTED':
      case 'PARTNER_REQUEST_DELETED':
        this.router.navigate(['/profile']);
        break;

      case 'TICKET_SHARE_REQUESTED':
      case 'TICKET_SHARE_ACCEPTED':
      case 'TICKET_SHARE_REJECTED':
      case 'TICKET_SHARE_CANCELED':
      case 'COUPLE_TICKET_SHARE':
      case 'COUPLE_TICKET_ASSIGNED':
      case 'COUPLE_TICKET_SHARE_FAILED':
        this.router.navigate(['/tickets']);
        break;

      case 'NEW_EVENT_ADD':
      case 'EVENT_UPDATE':
      case 'EVENT_DELETE':
      case 'EVENT_FIRST_REMINDER':
      case 'EVENT_SECOND_REMINDER':
        if (searchId) {
          this.router.navigate(['/event', searchId]).then((navigatedSuccess) => {
            if (!navigatedSuccess) {
              console.warn(`Failed to naviagert to [/event/${searchId}].`);
              this.router.navigate(['/tabs/events']);
            }
          }).catch((err) => {
            console.warn('Route failed.', err);
            this.router.navigate(['/tabs/events']);
          });
        } else {
          this.router.navigate(['/tabs/events']);
        }
        break;

      default:
        if (isEventRelated) {
          this.router.navigate(['/tabs/events']);
        } else {
          this.router.navigate(['/notifications']);
        }
        break;
    }
  }

  async showInAppNotification(title: string | undefined, body: string | undefined) {
    const toast = await this.toastController.create({
      header: title,
      message: body,
      icon: 'notifications-outline',
      duration: 5000,
      position: 'top',
      color: 'primary',
      buttons: [
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });
    await toast.present();
  }
}
